import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, name: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Update global tracking state
        if (typeof window !== 'undefined') {
          (window as any).__psUserLoggedIn = !!session?.user;
        }
        
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (typeof window !== 'undefined') {
        (window as any).__psUserLoggedIn = !!session?.user;
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, name: string) => {
    try {
      const redirectUrl = `${window.location.origin}/profilo`;
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            name: name,
          }
        }
      });

      if (error) {
        toast({
          variant: "destructive",
          title: "Errore durante la registrazione",
          description: error.message === "User already registered" 
            ? "Un account con questa email esiste già" 
            : error.message
        });
      } else {
        // Track signup event
        if (typeof window !== 'undefined' && (window as any).__cookieConsent?.analytics) {
          (window as any).dataLayer?.push({
            event: 'auth_signup',
            email_provided: !!email,
            timestamp: new Date().toISOString()
          });
        }
        
        toast({
          title: "Registrazione completata!",
          description: "Benvenuto su Progetto Startup"
        });
      }

      return { error };
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: "Si è verificato un errore durante la registrazione"
      });
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        toast({
          variant: "destructive",
          title: "Errore durante l'accesso",
          description: error.message === "Invalid login credentials" 
            ? "Credenziali non valide" 
            : error.message
        });
      } else {
        // Track login event
        if (typeof window !== 'undefined' && (window as any).__cookieConsent?.analytics) {
          (window as any).dataLayer?.push({
            event: 'auth_login',
            timestamp: new Date().toISOString()
          });
        }
        
        toast({
          title: "Accesso effettuato!",
          description: "Bentornato"
        });
      }

      return { error };
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: "Si è verificato un errore durante l'accesso"
      });
      return { error };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      
      // Track logout event
      if (typeof window !== 'undefined' && (window as any).__cookieConsent?.analytics) {
        (window as any).dataLayer?.push({
          event: 'auth_logout',
          timestamp: new Date().toISOString()
        });
      }
      
      toast({
        title: "Disconnesso",
        description: "A presto!"
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: "Si è verificato un errore durante la disconnessione"
      });
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};